<template>
  <BContainer
    class="p-0"
    fluid
  >
    <BRow
      class="py-2"
    >
      <BCol
        cols="12"
        md="8"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="tableInteractions.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="4"
      >
        <BFormGroup
          label="Activity"
          label-for="filterActivity"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="tableInteractions.selected.activity"
            placeholder="filter activity here"
            input-id="filterActivity"
            label="activity_name"
            clearable
            append-to-body
            :options="(tableInteractions.options.activities)"
            :loading="(tableInteractions.fetching.activities)"
            :disabled="(tableInteractions.busy || tableInteractions.fetching.activities)"
            :calculate-position="calculateDropPosition"
            @search="searchFilterActivities"
          >
            <template #option="{ activity_name }">
              <div
                class="text-break text-wrap"
                v-text="activity_name"
              />
            </template>
            <template #selected-option="{ activity_name }">
              <div
                class="w-175px w-md-75px w-lg-140px text-truncate"
                v-text="activity_name"
              />
            </template>
            <template #no-options="">
              no available activity
            </template>
          </VSelect>
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        class="mb-2"
      >
        <BButton
          size="sm"
          variant="primary"
          @click.prevent="createInteraction"
        >
          Add Interaction
        </BButton>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableInteractions"
      table-class="min-h-300px"
      :provider="tableProvider"
      :fields="tableInteractions.fields"
      :filter="tableInteractions.filter"
      :filter-on="tableInteractions.filterOn"
      :sort-by="tableInteractions.sortBy"
      :sort-desc="tableInteractions.sortDesc"
      :sort-direction="tableInteractions.sortDirection"
      :total-rows="tableInteractions.totalRows"
      :current-page="tableInteractions.currentPage"
      :per-page="tableInteractions.perPage"
      :stacked="tableInteractions.stacked"
      :busy="tableInteractions.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center">
          <BButton
            size="sm"
            variant="warning"
            :disabled="tableInteractions.busy"
            @click.prevent="viewInteraction(item)"
          >
            View
          </BButton>
        </div>
      </template>
    </CustomTable>

    <BModal
      ref="modalInteraction"
      scrollable
      centered
      hide-header
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      content-class="scrollable-tabs"
      modal-class="full-height modal-no-padding"
      aria-hidden="false"
      role="dialog"
    >
      <BTabs
        active-nav-item-class="font-weight-bold text-uppercase text-primary"
      >
        <BTab class="p-2 min-h-400px max-h-md-400px">
          <template #title>
            <div class="text-md-nowrap">
              <i
                :class="['fa-xl fad fa-file-lines', {
                  'text-danger fat fa-shake': vTabInteractionDetails()
                }]"
              />
              <b
                :class="['is-tab-active ml-1', {
                  'text-danger': vTabInteractionDetails()
                }]"
              >
                Details
              </b>
            </div>
          </template>
          <ValidationObserver
            ref="formInteraction"
          >
            <form
              role="form"
              novalidate
              @submit.prevent
            >
              <BRow>
                <BCol
                  cols="12"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="date"
                    name="date"
                    rules="required"
                  >
                    <BFormGroup
                      label="Date"
                      label-for="fDate"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <BFormInput
                        id="fDate"
                        v-model="formInteraction.interaction_date"
                        type="date"
                        :state="errors.length > 0 ? false : null"
                        :disabled="state.busy || state.editing"
                      />
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol
                  cols="12"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="activity_id"
                    name="activity"
                    rules="required"
                  >
                    <BFormGroup
                      label="Activity"
                      label-for="fActivity"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <VSelect
                        v-model="selected.activity"
                        input-id="fActivity"
                        label="activity_name"
                        clearable
                        append-to-body
                        placeholder="select activity here"
                        :options="options.activities"
                        :disabled="fetching.activities || state.busy || state.editing"
                        :loading="fetching.activities"
                        :class="{ 'is-invalid': errors.length > 0 }"
                        @search="searchActivities"
                      >
                        <template #option="{ activity_name }">
                          <div
                            class="text-break text-wrap"
                            v-text="activity_name"
                          />
                        </template>
                        <template #selected-option="{ activity_name }">
                          <div
                            class="w-225px w-md-325px text-truncate"
                            v-text="activity_name"
                          />
                        </template>
                        <template #no-options="">
                          no available activity
                        </template>
                      </VSelect>
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol
                  cols="12"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="description"
                    name="description"
                    rules="required|max:1000"
                  >
                    <BFormGroup
                      label="Description"
                      label-for="fDiscription"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <template #label>
                        <span class="d-flex justify-content-between">
                          Description
                          <span>{{ formInteraction.description ? formInteraction.description.length : 0 }} / 1000</span>
                        </span>
                      </template>
                      <BFormTextarea
                        id="fDiscription"
                        v-model="formInteraction.description"
                        placeholder="enter description here"
                        rows="4"
                        max-rows="10"
                        maxlength="1000"
                        :state="errors.length > 0 ? false : null"
                        :disabled="state.busy || state.editing"
                        @keydown.enter="preventEnterKey"
                      />
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>
              </BRow>
            </form>
          </ValidationObserver>
        </BTab>
        <BTab class="p-2 min-h-400px max-h-400px">
          <template #title>
            <div class="text-md-nowrap">
              <i
                :class="['fa-xl fad fa-paperclip', {
                  'text-danger fat fa-shake': vTabAttachments()
                }]"
              />
              <b
                :class="['is-tab-active ml-1', {
                  'text-danger': vTabAttachments()
                }]"
              >
                Attachments
              </b>
            </div>
          </template>
          <Attachment
            ref="attachmentUploader"
            :path="formInteraction.attachment_path"
            :files="formInteraction.attachments"
            :busy="state.busy"
            :show-uploader="!state.editing"
            @onUploaded="updateFormInteractionAttachments"
          />
        </BTab>
      </BTabs>
      <template #modal-footer="{ cancel }">
        <BButton
          v-if="!state.editing"
          variant="primary"
          :disabled="state.busy"
          @click.prevent="submitInteraction"
        >
          {{ state.editing ? 'Update' : 'Add' }} Interaction
        </BButton>
        <BButton

          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          {{ state.editing ? 'Close' : 'Discard' }}
        </BButton>
      </template>
    </BModal>
  </BContainer>
</template>
<script>
import QUERY from 'lodash'

import Attachment from '@/views/admin/customer-management/interactions/Attachment.vue'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MActivities from '@/mixins/maintenances/activities'

import { SAdminCustomerInteraction } from '@/services/admin'

export default {
  name: 'VAdminCustomerInteractions',

  components: {
    Attachment
  },

  mixins: [
    MActivities,
    MxFormatter,
    MxMisc
  ],

  props: {
    customer: {
      type: Object,
      required: true,
      default: () => ({
        id: ''
      })
    }
  },

  data () {
    return {
      state: {
        busy: false,
        editing: false
      },
      selected: {
        activityType: 'Interaction'
      },
      formInteraction: {
        customer_id: this.customer.id,
        interaction_date: '',
        activity_id: '',
        description: '',
        attachment_path: '',
        attachments: []
      },
      tableInteractions: {
        busy: false,
        fetching: {
          activities: false
        },
        selected: {
          activity: '',
          activityType: 'Interaction'
        },
        options: {
          activities: []
        },
        filter: {
          search: '',
          customer: this.customer.id,
          activity: ''
        },
        filterOn: [],
        stacked: 'sm',
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 6, key: 'action', class: 'text-center w-md-125px' },
          { mobile: 4, key: 'created_at', class: 'min-w-175px', formatter: this.dateTimeShortFormatter },
          { mobile: 1, key: 'user', label: 'Name', class: 'min-w-md-150px', formatter: this.getFullName, sortable: true },
          { mobile: 2, key: 'interaction_date', class: 'min-w-150px', formatter: this.dateShortFormatter, sortable: true },
          { mobile: 3, key: 'activity.activity_name', label: 'Activity', class: 'min-w-150px', sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  watch: {
    'selected.activity' (activity) {
      this.formInteraction.activity_id = activity?.id || ''
    },

    'tableInteractions.selected.activity' (activity) {
      this.tableInteractions.filter.activity = activity?.id || ''
    }
  },

  mounted () {
    this.getActivities(this.tableInteractions)
  },

  methods: {
    vTabInteractionDetails () {
      if (this.$refs?.formInteraction) {
        return Object.keys(this.$refs.formInteraction.errors).some(
          key => this.$refs.formInteraction.errors[key]?.length > 0
        )
      }

      return false
    },

    vTabAttachments () {
      try {
        if (this.$refs?.attachmentUploader) {
          const formDocument = this.$refs.attachmentUploader.$refs?.formDocument
          if (formDocument) {
            return Object.keys(formDocument.errors).some(
              key => formDocument.errors[key]?.length > 0
            )
          }
        }
      } catch {
        return false
      }

      return false
    },

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableInteractions.busy = true
      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        customer_id: filter.customer
      }

      return await SAdminCustomerInteraction.get(payload).then(
        ({ data }) => {
          this.tableInteractions.totalRows = data.total_rows
          return data.items
        }
      ).catch(
        () => {
          this.tableInteractions.totalRows = 0
          return []
        }
      ).finally(() => {
        this.tableInteractions.busy = false
      })
    },

    async searchFilterActivities (search, loading) {
      this.searchActivitiesProvider(this.tableInteractions, search, loading)
    },

    updateFormInteractionAttachments (attachment) {
      this.formInteraction = Object.assign(this.formInteraction, {
        attachment_path: attachment.attachment_path,
        attachments: attachment.attachments
      })
    },

    createInteraction () {
      this.state.editing = false

      this.formInteraction = Object.assign(this.formInteraction, {
        customer_id: this.customer.id,
        interaction_date: '',
        activity_id: '',
        description: '',
        attachment_path: '',
        attachments: []
      })

      this.getActivities(this)
      this.$refs.modalInteraction.show()
    },

    viewInteraction (interaction) {
      this.state.editing = true

      this.formInteraction = Object.assign(this.formInteraction, {
        customer_id: this.customer.id,
        interaction_date: interaction.interaction_date,
        activity_id: interaction.activity_id,
        description: interaction.description,
        attachment_path: interaction.attachment_path,
        attachments: interaction.attachments
      })

      this.getActivities(this).finally(() => {
        this.selected.activity = QUERY.find(this.options.activities, {
          id: interaction.activity_id
        }) || ''
      })

      this.$refs.modalInteraction.show()
    },

    async validateInteraction () {
      return new Promise((resolve, reject) => {
        this.$refs.formInteraction.validate().then(
          isFormValid => {
            const hasAttachments = this.formInteraction.attachments.length >= 1

            if (isFormValid && hasAttachments) {
              return resolve(isFormValid)
            }

            this.$refs.attachmentUploader.setErrors({
              attachments: ['Please upload at least one attachment.']
            })

            return reject(new Error('Invalid Form'))
          }
        )
      })
    },

    async submitInteraction () {
      return new Promise(resolve => {
        this.validateInteraction().then(
          () => {
            this.swalConfirm({
              html: this.state.editing ? 'Update this Interaction?' : 'Add Interaction?',
              confirmButtonText: this.state.editing ? 'Update' : 'Add',
              cancelButtonText: 'Discard',
              preConfirm: async () => {
                this.state.busy = true

                const createOrUpdate = async () => {
                  return this.state.editing ? this.putInteraction() : this.postInteraction()
                }

                return createOrUpdate().catch(
                  ({ message }) => {
                    if (message && message?.errors) {
                      this.$refs.formInteraction.setErrors(message.errors)
                    }
                  }
                ).finally(() => {
                  this.state.busy = false
                })
              }
            }).then(resolve)
          }
        ).catch(() => {
          this.swalInvalid()
        })
      })
    },

    async postInteraction () {
      return await SAdminCustomerInteraction.post(this.formInteraction).then(
        ({ data }) => {
          this.swalSuccess(data.message).then(() => {
            this.$refs.modalInteraction.hide()
            this.$refs.tableInteractions.refetch()
          })
          return data
        }
      )
    },

    async putInteraction () {
      return await SAdminCustomerInteraction.put(this.formInteraction).then(
        ({ data }) => data
      )
    }
  }
}
</script>
